import { BrandCard } from "@shltelemedicine/shl-ui-lib";
import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { isExpired } from "react-jwt";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { RouteConstants } from "../../constants";
import { getUserFromToken } from "../../utils/authorizationUtil";
import logger from "../../utils/logger";
import styles from "./ResetPassword.module.css";
import ResetPasswordForm from "./ResetPasswordForm/ResetPasswordForm";
import { ClientAccessUserPermission } from "../../models/token.interface";

export enum RedirectDestinations {
  Portal,
  App,
  Message,
  NoRedirection,
}

const getRedirectDestination = (
  clientAccessUserPermission?: ClientAccessUserPermission,
  deeplink?: string
): RedirectDestinations => {
  const isMobile = /Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
  if (isMobile) {
    switch (clientAccessUserPermission) {
      case ClientAccessUserPermission.Mobile:
        return deeplink
          ? RedirectDestinations.App
          : RedirectDestinations.Message;
      case ClientAccessUserPermission.Portal:
        return RedirectDestinations.Portal;
      case ClientAccessUserPermission.MobileAndPortal:
        return deeplink
          ? RedirectDestinations.App
          : RedirectDestinations.Portal;
      default:
        return RedirectDestinations.NoRedirection;
    }
  } else {
    switch (clientAccessUserPermission) {
      case ClientAccessUserPermission.Mobile:
        return RedirectDestinations.Message;
      case ClientAccessUserPermission.Portal:
        return RedirectDestinations.Portal;
      case ClientAccessUserPermission.MobileAndPortal:
        return RedirectDestinations.Portal;
      default:
        return RedirectDestinations.Portal;
    }
  }
};

const ResetPassword = () => {
  const { state } = useLocation();
  const { token } = state ?? {};
  const [userName, setUserName] = useState("");
  const [redirectDestination, setRedirectDestination] = useState<
    RedirectDestinations
  >(RedirectDestinations.Message);
  const [deeplink, setDeeplink] = useState<string | undefined>("");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const [error, setError] = useState<"token_expired" | "token_invalid" | null>(
    null
  );

  useEffect(() => {
    if (!token) {
      return;
    }

    // Validate token
    const userFromToken = getUserFromToken(token);
    if (!userFromToken) {
      setError("token_invalid");
      return;
    }

    if (isExpired(token)) {
      logger.error(ResetPassword.name, "Token expired.");
      setError("token_expired");
      return;
    }

    setUserName(userFromToken.UserName);
    setDeeplink(userFromToken.AppDeepLink);
    setRedirectDestination(
      getRedirectDestination(
        userFromToken.ClientAccessUserPermission,
        userFromToken.AppDeepLink
      )
    );
  }, [token]);

  const displayForm = !error && token && userName && !showSuccessMessage;

  return (
    <BrandCard className={styles.wrapper} contentClassName="z-index-front mb-3">
      {displayForm && (
        <ResetPasswordForm
          token={token}
          userName={userName}
          redirectDestination={redirectDestination}
          deeplink={deeplink}
          setShowSuccessMessage={setShowSuccessMessage}
        />
      )}
      {showSuccessMessage && !error ? <ResetSuccessfulMessage /> : ""}
      {error === "token_expired" ? <TokenExpiredMessage /> : ""}
      {error === "token_invalid" ? <TokenInvalidMessage /> : ""}
    </BrandCard>
  );
};

export default ResetPassword;

const TokenExpiredMessage = () => {
  return (
    <div className="text-center">
      <h5 className="mb-1">
        <FormattedMessage
          id="RESET_PASSWORD.LINK_EXPIRED_ERROR"
          defaultMessage="Your reset password link has expired"
        />
      </h5>
      <Link to={RouteConstants.FORGOT_PASSWORD_PATH}>
        <FormattedMessage
          id="RESET_PASSWORD.CLICK_HERE_TO_RESET"
          defaultMessage="Click here to reset password"
        />
      </Link>
    </div>
  );
};

const TokenInvalidMessage = () => {
  return (
    <div className="text-center">
      <h5 className="mb-1">
        <FormattedMessage
          id="RESET_PASSWORD.INVALID_LINK_ERROR"
          defaultMessage="Invalid reset password link"
        />
      </h5>
    </div>
  );
};

const ResetSuccessfulMessage = () => {
  return (
    <div className="text-center">
      <h5 className="mb-1">
        <FormattedMessage
          id="RESET_PASSWORD.SUCCESS_TITLE"
          defaultMessage="Password reset successfully!"
        />
      </h5>
      <FormattedMessage
        id="RESET_PASSWORD.SUCCESS_MESSAGE"
        defaultMessage="Please launch the SmartHeart Pro application on your mobile device to log in."
      />
    </div>
  );
};
